// src/App.js
import React, { useState, useEffect } from "react";
import { Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import Customers from "./components/Customers";
import NewCustomer from "./components/NewCustomer";
import Customer from "./components/Customer";
import MonthlyReport from "./components/MonthlyReport";
import Login from "./components/Login/Login";
import Register from "./components/Login/Register";
import Users from "./components/Login/Users";
import ChangePassword from "./components/Login/ChangePassword";
import PartnerPhone from "./components/PartnerPhone";
import PriceProductsAll from "./components/PriceProductsAll";
import UniqueProducts from "./components/UniqueProducts";
import ServerStatus from "./components/ServerStatus";
import PriceProductsCity from "./components/PriceProductsCity";
import PriceProductsGovernorate from "./components/PriceProductsGovernorate";
import PriceProducts from "./components/PriceProducts";
import { ToastContainer } from "react-toastify";
import { Bounce } from "react-toastify";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./components/Customer.css";
import ScrollToTopButton from "./components/ScrollToTopButton";
import ScrollToBottomButton from "./components/ScrollToBottomButton";
import Requests from "./components/Requests";
import NewRequest from "./components/Requests/NewRequest";
import Texts from "./components/Texts";
import UserLogs from "./components/Login/UserLogs";
import Subscriptions from "./components/Subscriptions";
import CustomerOrders from "./components/CustomerOrders";
import instance from "./instance";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const clearLocalSession = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("userId");
    localStorage.removeItem("role");
    localStorage.removeItem("lastLoginTime");
    localStorage.removeItem("customerName");
    setIsAuthenticated(false);
  };

  useEffect(() => {
    const tokenAtRequest = localStorage.getItem("token");
    if (tokenAtRequest) {
      instance
        .get(`users/verify-token`)
        .then((response) => {
          // Ignore result if the user already logged in with a new token
          if (localStorage.getItem("token") !== tokenAtRequest) return;
          setIsAuthenticated(response.status === 200);
        })
        .catch((error) => {
          console.error("Token verification failed:", error);
          // Only clear if this response still matches the token we verified
          if (localStorage.getItem("token") === tokenAtRequest) {
            localStorage.removeItem("token");
            setIsAuthenticated(false);
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (token) => {
    localStorage.setItem("token", token);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      const response = await instance.post(
        "users/logout",
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error("Logout failed");
      }

      clearLocalSession();
      toast.success("Logout successful.");
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      // Clear local session anyway so the user is not stuck authenticated
      clearLocalSession();
      toast.error("An error occurred while logging out.");
      navigate("/login");
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="loading-wrapper">
        <div className="loading-circle"></div>
        <p className="loading">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login setAuth={handleLogin} />} />
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Customers handleLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/register"
          element={isAuthenticated ? <Register /> : <Navigate to="/login" />}
        />
        <Route
          path="/users"
          element={isAuthenticated ? <Users /> : <Navigate to="/login" />}
        />
        <Route
          path="/ChangePassword"
          element={
            isAuthenticated ? <ChangePassword /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/new-customer"
          element={isAuthenticated ? <NewCustomer /> : <Navigate to="/login" />}
        />
        <Route
          path="/customer"
          element={isAuthenticated ? <Customer /> : <Navigate to="/login" />}
        />
        <Route
          path="/monthly"
          element={
            isAuthenticated ? <MonthlyReport /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/ServerStatus"
          element={
            isAuthenticated ? <ServerStatus /> : <Navigate to="/login" />
          }
        />
         <Route
          path="/Texts"
          element={
            isAuthenticated ? <Texts /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/phone"
          element={
            isAuthenticated ? <PartnerPhone /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/logs"
          element={isAuthenticated ? <UserLogs /> : <Navigate to="/login" />}
        />
        <Route
          path="/price"
          element={
            isAuthenticated ? <PriceProducts /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/price-governorate"
          element={
            isAuthenticated ? (
              <PriceProductsGovernorate />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/price-city"
          element={
            isAuthenticated ? <PriceProductsCity /> : <Navigate to="/login" />
          }
        />
         <Route
          path="/unique"
          element={
            isAuthenticated ? <UniqueProducts /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/price-all"
          element={
            isAuthenticated ? <PriceProductsAll /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/support"
          element={isAuthenticated ? <Requests /> : <Navigate to="/login" />}
        />
        <Route
          path="/NewRequest"
          element={isAuthenticated ? <NewRequest /> : <Navigate to="/login" />}
        />
        <Route
          path="/subscriptions"
          element={isAuthenticated ? <Subscriptions /> : <Navigate to="/login" />}
        />
        <Route
          path="/customer-orders"
          element={isAuthenticated ? <CustomerOrders /> : <Navigate to="/login" />}
        />
      </Routes>

      {(location.pathname === "/" ||
        location.pathname === "/logs" ||
        location.pathname === "/support" ||
        location.pathname === "/phone" ||
        location.pathname === "/price" ||
        location.pathname === "/price-governorate" ||
        location.pathname === "/price-city" ||
        location.pathname === "/price-all" ||
        location.pathname === "/subscriptions" ||
        location.pathname === "/customer-orders") && <ScrollToBottomButton />}
      {(location.pathname === "/" ||
        location.pathname === "/logs" ||
        location.pathname === "/support" ||
        location.pathname === "/phone" ||
        location.pathname === "/price" ||
        location.pathname === "/price-governorate" ||
        location.pathname === "/price-city" ||
        location.pathname === "/price-all" ||
        location.pathname === "/subscriptions" ||
        location.pathname === "/customer-orders") && <ScrollToTopButton />}
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Bounce}
      />
    </>
  );
}

export default App;
